'use client';

import type { ILocationResult } from '@components/searchCity';
import type { LeagueEvent } from '@interfaces/APITravel';
import { Button } from '@v2/ui/Button/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@v2/ui/Dialog/Dialog';
import { useEffect, useState } from 'react';
import {
  coerceHotelSearchArea,
  locationForArea,
  type HotelSearchArea,
} from '../hotel-search-area';
import { isMapCoordinate } from '../hotel-presentation';
import { HotelAreaPicker } from './HotelAreaPicker';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  event?: LeagueEvent;
  downtown: ILocationResult | null;
  stadium: ILocationResult | null;
  area: HotelSearchArea;
  location: ILocationResult | null | undefined;
  onApply: (next: { area: HotelSearchArea; location: ILocationResult }) => void;
};

function preventDismissForPopover(event: {
  preventDefault: () => void;
  target: EventTarget;
}) {
  const target = event.target as Element | null;
  if (target?.closest?.('[data-radix-popper-content-wrapper]')) {
    event.preventDefault();
  }
}

export function HotelAreaDialog({
  open,
  onOpenChange,
  event,
  downtown,
  stadium,
  area,
  location,
  onApply,
}: Props) {
  const committedArea = coerceHotelSearchArea(area);
  const [draftArea, setDraftArea] = useState<HotelSearchArea>(committedArea);
  const [customLocation, setCustomLocation] = useState<ILocationResult | null>(
    committedArea === 'custom' ? (location ?? null) : null
  );
  // Always prefill the location search with the current area's location name
  // so customers can see what the area picker selected and edit if needed.
  const [customQuery, setCustomQuery] = useState(location?.name ?? '');

  useEffect(() => {
    if (!open) return;
    setDraftArea(committedArea);
    setCustomLocation(committedArea === 'custom' ? (location ?? null) : null);
    setCustomQuery(location?.name ?? '');
  }, [open, committedArea, location]);

  const draftLocation = locationForArea({
    area: draftArea,
    stadium,
    downtown,
    custom: customLocation,
  });
  const canApply = Boolean(
    draftLocation &&
    draftLocation.latitude != null &&
    draftLocation.longitude != null &&
    isMapCoordinate(
      Number(draftLocation.latitude),
      Number(draftLocation.longitude)
    )
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="flex max-w-[560px] flex-col gap-5 overflow-visible p-6 pt-8 md:max-w-[560px]"
        onPointerDownOutside={preventDismissForPopover}
        onFocusOutside={preventDismissForPopover}
        onInteractOutside={preventDismissForPopover}
      >
        <DialogHeader className="shrink-0">
          <DialogTitle className="text-2xl font-bold text-gray-900">
            Choose your area to search
          </DialogTitle>
          <DialogDescription className="text-start text-sm font-normal text-gray-600">
            Find hotels close to what matters most to you.
          </DialogDescription>
        </DialogHeader>
        <HotelAreaPicker
          event={event}
          downtown={downtown}
          value={draftArea}
          customQuery={customQuery}
          showLandmarkSearch
          onSelectArea={(next) => {
            setDraftArea(next);
            setCustomQuery(
              next === 'downtown'
                ? (downtown?.name ?? '')
                : (stadium?.name ?? '')
            );
            setCustomLocation(null);
          }}
          onCustomQueryChange={(query) => {
            setCustomQuery(query);
            setDraftArea('custom');
            setCustomLocation(null);
          }}
          onSelectCustom={(next) => {
            setDraftArea('custom');
            setCustomLocation(next);
            setCustomQuery(next.name);
          }}
        />
        <DialogFooter className="shrink-0 gap-3 sm:justify-end">
          <Button
            type="button"
            variant="secondary"
            className="min-h-11 min-w-[7.5rem]"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            className="min-h-11 min-w-[7.5rem]"
            disabled={!canApply}
            onClick={() => {
              if (!draftLocation) return;
              onApply({ area: draftArea, location: draftLocation });
              onOpenChange(false);
            }}
          >
            Apply Area
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
